import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

const DEFAULT_LOADING_FALLBACK_LOGO_URL = "https://placehold.co/60?text=Logo";

const verbData = [
	{
		category: "Spatial Thinking & Analysis",
		verbs: [
			"comparing",
			"correlating",
			"evaluating",
			"predicting",
			"inferring",
			"assessing",
			"identifying",
			"overlaying",
			"modeling",
			"detecting",
			"measuring",
			"discovering",
			"zoning",
			"estimating",
			"surveying",
			"calculating",
			"clustering",
		],
	},
	{
		category: "Remote Sensing & Data Collection",
		verbs: [
			"sensing",
			"capturing",
			"imaging",
			"flying",
			"orbiting",
			"scanning",
			"observing",
			"surveying",
			"sampling",
			"monitoring",
			"logging",
			"collecting",
			"detecting",
			"transmitting",
			"registering",
			"acquiring",
			"uploading",
			"syncing",
		],
	},
	{
		category: "Navigation & Wayfinding",
		verbs: [
			"navigating",
			"routing",
			"wayfinding",
			"orienting",
			"positioning",
			"locating",
			"tracking",
			"following",
			"surveying",
			"traversing",
			"directing",
			"guiding",
			"exploring",
			"patrolling",
			"cruising",
			"charting",
			"driving",
			"hiking",
			"biking",
			"wandering",
			"searching",
			"pathfinding",
			"triangulating",
			"scanning",
			"zooming",
			"panning",
			"calibrating",
		],
	},
	{
		category: "Made-Up Spatial Verbs",
		verbs: [
			"geobfuscating",
			"buffernating",
			"spatialogizing",
			"reprojectilating",
			"misjoinifying",
			"confusifying",
			"snapplicating",
			"underlayzoning",
			"clusterbunking",
			"relatograding",
			"topolocating",
			"spaghettifying",
			"spatialyzing",
			"mapcrastinating",
			"analytispreading",
			"krigeifying",
			"dissolutifying",
			"cartofudging",
			"queryfluffing",
			"layergassing",
			"zoommaxxing",
			"panjiving",
			"geosnapping",
			"datadoodling",
			"spatialwhimsifying",
			"mapmuddling",
			"geobabbling",
			"cartofluffing",
			"spatialjibbering",
			"layerblurring",
			"zoomfuzzing",
		],
	},
	{
		category: "Old-School Surveying",
		verbs: [
			"surveying",
			"triangulating",
			"transiting",
			"levelling",
			"chaining",
			"pacing",
			"plumbing",
			"sighting",
			"benchmarking",
			"staking",
			"flagging",
			"back-sighting",
			"fore-sighting",
			"ranging",
			"sketching",
			"plotting",
			"notebooking",
			"reading",
			"shooting",
			"calling",
			"calculating",
			"marking",
			"taping",
			"checking",
			"adjusting",
			"turning",
			"setting out",
			"staking out",
			"cross-sectioning",
			"running levels",
			"holding",
			"scribbling",
			"measuring",
			"calibrating",
			"hand-sketching",
			"traversing",
			"closing",
			"level-booking",
			"dialling in",
			"brassing",
		],
	},
	{
		category: "Funny Old-School Surveying Verbs",
		verbs: [
			"plumbfuddling",
			"tapespaghettiing",
			"rodjuggling",
			"benchscratching",
			"transitnaping",
			"chainwhispering",
			"backsightbickering",
			"bookingitwrong",
			"sightlosing",
			"rangewandering",
			"chalksploding",
		],
	},
];

export interface LoadingSceneProps {
	fallbackLogoUrl?: string;
	speed?: number;
	typewriterSpeed?: number;
	logoUrl?: string;
	noText?: boolean;
}

export const LoadingScene: React.FC<LoadingSceneProps> = ({
	fallbackLogoUrl = DEFAULT_LOADING_FALLBACK_LOGO_URL,
	speed = 2500,
	typewriterSpeed = 100,
	logoUrl,
	noText = false,
}) => {
	const [currentCategoryIndex, setCurrentCategoryIndex] = React.useState(0);
	const [wordIndicesPerCategory, setWordIndicesPerCategory] = React.useState(
		() => verbData.map(() => 0),
	);
	const [displayedText, setDisplayedText] = React.useState("");
	const [isTyping, setIsTyping] = React.useState(true);
	const [failedLogoUrl, setFailedLogoUrl] = React.useState<string>();
	const [loadedLogoUrl, setLoadedLogoUrl] = React.useState<string>();
	const isLogoLoaded = Boolean(logoUrl) && logoUrl === loadedLogoUrl;
	const isLogoFailed = Boolean(logoUrl) && logoUrl === failedLogoUrl;
	const isFallbackLogo = logoUrl === fallbackLogoUrl;
	const isVisibleLogo = isFallbackLogo || isLogoLoaded;
	const visibleLogoUrl = isLogoLoaded ? logoUrl : fallbackLogoUrl;
	const logoState = isVisibleLogo
		? "loaded"
		: isLogoFailed
			? "failed"
			: logoUrl
				? "loading"
				: "fallback";

	// Word cycling effect
	React.useEffect(() => {
		const interval = setInterval(() => {
			// Clear current text and start typing new word
			setDisplayedText("");
			setIsTyping(true);

			// Update word index for current category
			setWordIndicesPerCategory((prev) => {
				const newIndices = [...prev];
				const currentCategory = verbData[currentCategoryIndex];
				if (currentCategory) {
					const currentWordIndex = newIndices[currentCategoryIndex] ?? 0;
					const isLastWordInCategory =
						currentWordIndex >= currentCategory.verbs.length - 1;
					newIndices[currentCategoryIndex] = isLastWordInCategory
						? 0
						: currentWordIndex + 1;
				}
				return newIndices;
			});

			// Move to next category
			setCurrentCategoryIndex((prev) => {
				const isLastCategory = prev >= verbData.length - 1;
				return isLastCategory ? 0 : prev + 1;
			});
		}, speed);

		return () => clearInterval(interval);
	}, [currentCategoryIndex, speed]);

	const currentWordIndex = wordIndicesPerCategory[currentCategoryIndex] ?? 0;
	const currentVerb =
		verbData[currentCategoryIndex]?.verbs[currentWordIndex] ?? "loading";

	// Typewriter effect
	React.useEffect(() => {
		if (!isTyping) return;

		const fullText = currentVerb + "...";
		let charIndex = 0;

		const typeInterval = setInterval(() => {
			if (charIndex <= fullText.length) {
				setDisplayedText(fullText.slice(0, charIndex));
				charIndex++;
			} else {
				setIsTyping(false);
				clearInterval(typeInterval);
			}
		}, typewriterSpeed);

		return () => clearInterval(typeInterval);
	}, [currentVerb, isTyping, typewriterSpeed]);

	return (
		<Stack
			spacing={1}
			alignItems="center">
			<Box
				className="pulse"
				sx={{
					width: 60,
					height: 60,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					animation: "pulse 2s ease-in-out infinite",
					"@keyframes pulse": {
						"0%": {
							transform: "scale(1)",
							opacity: 0.8,
						},
						"50%": {
							transform: "scale(1.05)",
							opacity: 1,
						},
						"100%": {
							transform: "scale(1)",
							opacity: 0.8,
						},
					},
				}}>
				<Box
					alt=""
					component="img"
					data-logo-state={logoState}
					data-testid="LoadingSceneLogo"
					src={visibleLogoUrl}
					sx={{
						width: "100%",
						height: "100%",
						objectFit: "contain",
					}}
				/>
				{logoUrl && !isFallbackLogo && !isLogoLoaded && !isLogoFailed && (
					<Box
						alt=""
						aria-hidden
						component="img"
						data-testid="LoadingSceneLogoPreload"
						key={logoUrl}
						onError={() => setFailedLogoUrl(logoUrl)}
						onLoad={() => setLoadedLogoUrl(logoUrl)}
						src={logoUrl}
						sx={{ height: 0, position: "absolute", width: 0 }}
					/>
				)}
			</Box>

			{!noText && (
				<Box
					sx={{
						minHeight: 32,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}>
					<Typography
						variant="overline"
						sx={{
							color: "var(--mui-palette-text-primary)",
							fontWeight: 500,
							textAlign: "center",
							fontFamily: "monospace",
							minWidth: "120px",
						}}>
						{displayedText}
					</Typography>
				</Box>
			)}
		</Stack>
	);
};
